import { useEffect, useMemo, useState } from "react";
import { Feature } from "geojson";
import { isEqual } from "lodash";

import { apiSlice, selectLayerByVersionAndLayerKey } from "fond/api";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

/**
 * Helper function to clone features and add layerId property.
 * This allows features from different layers to be distinguished during save.
 */
const addLayerIdToFeatures = <T extends Feature>(features: T[] | undefined, layerId: string): T[] => {
  if (!features) return [];
  return structuredClone(features).map((f) => ({
    ...f,
    properties: { ...f.properties, layerId },
  }));
};

interface LayerFeaturesResult {
  layerKey: string;
  features: Feature[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Custom hook to fetch features for multiple layers.
 *
 * This hook fetches feature data for all specified layer keys and combines them
 * into a single array. Each feature is tagged with its layerId for later identification.
 *
 * Uses RTK Query's dispatch-based fetching to avoid React hooks limitations.
 *
 * @param versionId - The version ID to fetch layers from
 * @param layerKeys - Array of layer keys (e.g., ["demand", "leadins"])
 * @returns Object containing combined features, loading state, and error state
 *
 * @example
 * const { features, isLoading } = useMultiLayerFeatures(versionId, ["demand", "leadins"]);
 */
export const useMultiLayerFeatures = (
  versionId: string,
  layerKeys: string[]
): {
  features: Feature[];
  isLoading: boolean;
  isError: boolean;
} => {
  const dispatch = useAppDispatch();
  const [layerResults, setLayerResults] = useState<LayerFeaturesResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get all layer metadata in one selector call with deep equality check
  const layers = useAppSelector((state) => {
    return layerKeys.map((layerKey) => {
      const layer = selectLayerByVersionAndLayerKey(state, { versionId, layerId: layerKey });
      return {
        layerKey,
        layerId: layer?.ID, // Extract just the ID we need
      };
    });
  }, isEqual);

  // Fetch features for all layers using dispatch
  useEffect(() => {
    // Skip if no layers
    if (layerKeys.length === 0) {
      setLayerResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Create promises for all layer fetches
    const fetchPromises = layers.map(async ({ layerKey, layerId }) => {
      if (!layerId) {
        return {
          layerKey,
          features: [],
          isLoading: false,
          isError: false,
        };
      }

      try {
        // Use RTK Query's initiate method to fetch data
        const result = await dispatch(apiSlice.endpoints.getFeatures.initiate({ layerId }, { forceRefetch: false })).unwrap();

        return {
          layerKey,
          features: addLayerIdToFeatures(result, layerKey),
          isLoading: false,
          isError: false,
        };
      } catch (error) {
        console.error(`Failed to fetch features for layer ${layerKey}:`, error);
        return {
          layerKey,
          features: [],
          isLoading: false,
          isError: true,
        };
      }
    });

    // Wait for all fetches to complete
    Promise.all(fetchPromises).then((results) => {
      setLayerResults(results);
      setIsLoading(false);
    });
  }, [dispatch, layers, layerKeys.length]);

  // Combine results
  return useMemo(() => {
    const features: Feature[] = [];
    let isError = false;

    layerResults.forEach((result) => {
      features.push(...result.features);
      if (result.isError) {
        isError = true;
      }
    });

    return {
      features,
      isLoading,
      isError,
    };
  }, [layerResults, isLoading]);
};
